/**
 * New translation: collection → one original document → target language
 * (optional source language) → Start. Creates a solution run of kind
 * `translate`.
 *
 * Blocks up front what the backend would refuse: formats Document Translation
 * cannot take, files over its size cap, translations of translations, and
 * collections the person does not own (adding a document is an owner action).
 * The backend re-checks every one of these.
 */

import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Group, Loader, Select, Stack, Text, TextInput } from "@mantine/core";
import { IconLanguage } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  CollectionDocumentPicker,
  type CollectionSelection,
} from "@/features/collections/CollectionDocumentPicker";
import { getIndex, type IndexDetailOut } from "@/shared/api/indexes";
import { createRun, type RunDetailOut } from "@/shared/api/solutionRuns";
import { getTranslateOptions, type TranslationOptionsOut } from "@/shared/api/solutionsTranslate";
import { toastApiError } from "@/shared/ui/toast";
import { isPdf, translateCandidates, type TranslateParams } from "./translateMeta";

interface Props {
  onCreated: (run: RunDetailOut) => void;
}

export function TranslateForm({ onCreated }: Props) {
  const getToken = useAccessToken();
  const [options, setOptions] = useState<TranslationOptionsOut | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [collection, setCollection] = useState<CollectionSelection[]>([]);
  const [detail, setDetail] = useState<IndexDetailOut | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setOptions(await getTranslateOptions(await getToken()));
      } catch (err) {
        setOptionsError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [getToken]);

  const indexId = collection[0]?.indexId ?? null;
  useEffect(() => {
    setDetail(null);
    setDocumentId(null);
    if (!indexId) return;
    void (async () => {
      try {
        setDetail(await getIndex(await getToken(), indexId));
      } catch (err) {
        toastApiError("Could not load the collection's documents", err);
      }
    })();
  }, [getToken, indexId]);

  const candidates = useMemo(
    () =>
      detail && options
        ? translateCandidates(detail.documents, options.document_extensions, options.max_file_bytes)
        : [],
    [detail, options],
  );
  const chosen = candidates.find((c) => c.doc.document_id === documentId) ?? null;
  const languages = useMemo(
    () =>
      (options?.languages ?? []).map((l) => ({
        value: l.code,
        label:
          l.name === l.native_name
            ? `${l.name} (${l.code})`
            : `${l.name} — ${l.native_name} (${l.code})`,
      })),
    [options],
  );

  // Suggest a run name from the document and language until the user edits it.
  const [nameTouched, setNameTouched] = useState(false);
  useEffect(() => {
    if (nameTouched) return;
    const doc = chosen?.doc.display_name;
    setName(doc && target ? `${doc} → ${target.toUpperCase()}` : "");
  }, [chosen, target, nameTouched]);

  if (optionsError) {
    return (
      <Alert color="red" title="Could not load the Translator's languages">
        {optionsError}
      </Alert>
    );
  }
  if (!options) return <Loader size="sm" aria-label="Loading translation options" />;
  if (!options.available) {
    return (
      <Alert color="yellow" title="Translation is not available here">
        This environment has no Translator configured.
      </Alert>
    );
  }

  const notOwner = detail !== null && !detail.is_owner;
  const sameLanguage = Boolean(source && target && source.toLowerCase() === target.toLowerCase());
  const canStart =
    Boolean(indexId && chosen && !chosen.disabledReason && target && name.trim()) &&
    !notOwner &&
    !sameLanguage &&
    !busy;

  const start = async () => {
    if (!indexId || !documentId || !target) return;
    setBusy(true);
    try {
      const params: TranslateParams = { target_language: target, source_language: source };
      const run = await createRun(await getToken(), {
        kind: "translate",
        name: name.trim(),
        collections: [{ index_id: indexId, document_ids: [documentId] }],
        upload_ids: [],
        params: { ...params },
      });
      setDocumentId(null);
      setNameTouched(false);
      onCreated(run);
    } catch (err) {
      toastApiError("Could not start the translation", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="sm" data-testid="translate-form">
      <CollectionDocumentPicker
        label="Collection"
        value={collection}
        onChange={setCollection}
        multiple={false}
        allowDocumentSubset={false}
      />
      {notOwner && (
        <Alert color="yellow" variant="light">
          This collection is shared with you. Only its owner can add a translation to it.
        </Alert>
      )}
      {indexId && detail === null && <Loader size="xs" aria-label="Loading documents" />}
      {detail && (
        <Select
          label="Document"
          description="Originals only. The translation is added to the same collection."
          placeholder={candidates.length ? "Choose a document" : "No documents"}
          data={candidates.map((c) => ({
            value: c.doc.document_id,
            label:
              c.doc.display_name +
              (c.translations.length ? ` · Translations: ${c.translations.join(", ")}` : "") +
              (c.disabledReason ? ` — ${c.disabledReason}` : ""),
            disabled: Boolean(c.disabledReason),
          }))}
          value={documentId}
          onChange={setDocumentId}
          searchable
          disabled={notOwner}
        />
      )}
      {chosen && isPdf(chosen.doc) && (
        <Text size="xs" c="dimmed">
          {options.scanned_pdf_note}
        </Text>
      )}
      <Group grow align="flex-start">
        <Select
          label="Translate to"
          placeholder="Target language"
          data={languages}
          value={target}
          onChange={setTarget}
          searchable
          required
        />
        <Select
          label="From"
          placeholder="Auto-detect"
          data={languages}
          value={source}
          onChange={setSource}
          searchable
          clearable
          error={sameLanguage ? "Same as the target language" : undefined}
        />
      </Group>
      <TextInput
        label="Run name"
        value={name}
        onChange={(e) => {
          setNameTouched(true);
          setName(e.currentTarget.value);
        }}
        maxLength={200}
      />
      <Text size="xs" c="dimmed">
        Accepted formats: {options.document_extensions.join(", ")} · up to{" "}
        {Math.round(options.max_file_bytes / (1024 * 1024))} MB.
      </Text>
      <Group justify="flex-end">
        <Button
          leftSection={<IconLanguage size={16} />}
          onClick={() => void start()}
          disabled={!canStart}
          loading={busy}
        >
          Start translation
        </Button>
      </Group>
    </Stack>
  );
}
